import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Box, IconButton, Tooltip} from "@mui/material";

import {FilterAlt, Transform} from "@mui/icons-material";
import {green, red} from "@mui/material/colors";
import {format} from "date-fns";
import {AgGridReact} from "ag-grid-react";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFnsV3";
import {useDispatch} from "react-redux";
import {withTranslation} from "react-i18next";
import { isEmpty, isEqual, remove, size, isNull, includes, values, split, replace, isArray, find, cloneDeep
} from "lodash";

import SplitterContainer from "@plannel/components/SplitterContainer";
import TypeToggleButton from "@plannel/components/ToggleButton";
import DataState from "@plannel/components/aggrid/DataState";
import Snackbar from "@plannel/components/Snackbar";
import ChartComposed from "@plannel/components/rechart/ChartComposed";
import PaginationContainer from "@plannel/components/PaginationContainer";
import {DpDefaultGridSetting} from "@plannel/components/aggrid/DefaultGridSetting";
import "@plannel/scss/components/CustomGrid.scss";

import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import codeUtil from "@plannel/pages/utils/code-util";
import dateUtils from "@plannel/utils/date-util";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import colorUtil from "@plannel/pages/utils/color-util";
import AbcXyzChip, { AbcXyzChipInline } from "@plannel/components/aggrid/AbcXyzChip";
import numberUtil from "@plannel/utils/number-util";
import axiosUtils from "@plannel/utils/axios-bigint";
import WorkbenchUtil from "@plannel/pages/demand-plan/dp-workbench/WorkbenchUtil";

import DpPlanActualService from "@plannel/services/dp/dp-plan-actual-service";
import CustomHeaderMenuIcon from "@plannel/components/aggrid/CustomHeaderMenuIcon";
import MeasureModal from "@plannel/components/modal/MeasureModal";
import p13nService from "@plannel/services/system/p13n-service";
import BucketToggleButton from "@plannel/components/BucketToggleButton"
import CustomerHrchyTreeFilter from "@plannel/components/filter/CustomerHrchyTreeFilter";
import ItemHrchyTreeFilter from "@plannel/components/filter/ItemHrchyTreeFilter";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import {DPFilterModal} from "../dp-workbench/dp-workbench-modal/FilterModal";
import planConfigService from "@plannel/services/data/plan-config-service";
import measureService from "@plannel/services/data/measure-service";
import DpVersionSelect from "../dp-workbench/DpVersionSelect";
import AbcXyzFilterModal from "@plannel/components/modal/AbcXyzFilterModal";
import FilterStatusPanel, {extractFilterInfo, getFilterStore} from "@plannel/components/aggrid/FilterStatusPanel";
import WorkbenchEditable from "@plannel/pages/demand-plan/dp-workbench/WorkbenchEditable";
import DpExcelButtons from "@plannel/pages/demand-plan/dp-workbench/dp-workbench-modal/DpExcelButtons";
import CodeUtil from "@plannel/pages/utils/code-util";

const DpPlanVsActual = ({ t, title, viewName }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const sortType = "asc";
  const CUSTHRCHY = "customerHrchy";
  const ITEMHRCHY = "itemHrchy";
  const ITEM = "item";
  const PERIOD_START = "periodStart";
  const PERIOD_END = "periodEnd";
  const isCum = isEqual(viewName, "DP_ACTUAL_PLAN_CUM");
  const { RELEASED } = codeUtil.dpVersion;

  const TODAY = new ZDate();
  const MAX_END_DATE = new ZDate(TODAY.getFullYear() + 9, TODAY.getMonth() + 1, 0, 23, 59, 59);
  const START_DATE = new ZDate(TODAY.getFullYear(), TODAY.getMonth() - 6, 1, 0, 0, 0);
  const { buckets: { DAY, WEEK, MONTH, QUARTER, PARTIAL_WEEK }, toggleType: { AMT, QTY }, customer, item, muicolor, filterType, getCurrencySymbol, dpPlanActualMeasures } = codeUtil;
  const { DP_ACTUAL_PLAN_ORDER_ITEM, DP_ACTUAL_PLAN_CUM_ORDER_ITEM } = codeUtil.dpP13N;
  const { colorSet, snackBarMessageColor } = colorUtil;
  const { MF_AMT, MF_QTY, FF_AMT, FF_QTY } = codeUtil.dpMeasure;
  const { DEMAND_PLAN_A, DEMAND_PLAN_Q, BUDGET_PLAN_Q, BUDGET_PLAN_A } = dpPlanActualMeasures;
  const qtyMeasure = useMemo(
    () => values(dpPlanActualMeasures).filter(measure => isEqual(measure?.type, QTY)).map(measure => measure.name),
    [dpPlanActualMeasures, QTY]);
  const amtMeasure = useMemo(
    () => values(dpPlanActualMeasures).filter(measure => isEqual(measure?.type, AMT)).map(measure => measure.name),
    [dpPlanActualMeasures, AMT]);

  const measureNmByCd = useMemo(() => {
    const nmByCd = {};
    for (const key of Object.keys(dpPlanActualMeasures)) {
      const measureVal = dpPlanActualMeasures[key];
      nmByCd[measureVal?.cd ?? key] = measureVal.name;
    }
    return nmByCd;
  }, [dpPlanActualMeasures]);

  const dpMeasureDefaultNmByCd = useMemo(() => {
    const nmByCd = {};
    for (const measureVal of values(codeUtil.dpMeasure)) {
      if (measureVal?.cd) {
        nmByCd[measureVal.cd] = measureVal.name;
      }
    }
    return nmByCd;
  }, []);

  const { CHECKBOX_SELECTED } = GridUtils.rowSelectedSource;
  const userData = JSON.parse(localStorage.getItem("user"));
  const responseRef = useRef();

  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const [gridData, setGridData] = useState([]);
  const [gridApi, setGridApi] = useState(null);
  const [columnDefs, setColumnDefs] = useState([]);
  const exportColumnDefs = useRef([]);
  const [planConfigObj, setPlanConfigObj] = useState(reduxViewState?.planConfigObj ?? {});

  const [chartData, setChartData] = useState([]);
  const [formatHeaders, setFormatHeaders] = useState([]);
  const [startDate, setStartDate] = useState(reduxViewState?.startDate ?? START_DATE);
  const [endDate, setEndDate] = useState(reduxViewState?.endDate ?? new ZDate());
  const [timeDimension, setTimeDimension] = useState(reduxViewState?.timeDimension ?? MONTH);
  const [selectVersion, setSelectVersion] = useState(reduxViewState?.selectVersion ?? {});
  const [vListReload, setVListReload] = useState(false);

  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? {});
  const [itemFilterList, setItemFilterList] = useState(reduxViewState?.itemFilterList ?? {});
  const [customerFilterList, setCustomerFilterList] = useState(reduxViewState?.customerFilterList ?? {});
  const [customerFilterColor, setCustomerFilterColor] = useState(size(reduxViewState?.customerFilterList) > 0 ? muicolor.INFO : muicolor.DEFAULT);
  const [itemFilterColor, setItemFilterColor] = useState(size(reduxViewState?.itemFilterList) > 0 ? muicolor.INFO : muicolor.DEFAULT);
  const [custFilterType, setCustFilterType] = useState(reduxViewState?.custFilterType ?? [filterType.or.cd, filterType.equal.cd]);
  const [itemFilterType, setItemFilterType] = useState(reduxViewState?.itemFilterType ?? [filterType.or.cd, filterType.equal.cd]);
  const [customValue, setCustomValue] = useState([]);
  const [itemCustomValue, setItemCustomValue] = useState(reduxViewState?.filter?.category ?? []);
  const [treeload, setTreeload] = useState(false);
  const [itemTreeLoad, setItemTreeLoad] = useState(false);
  const [modelCode, setModelCode] = useState(customer.name);
  const [filterData, setFilterData] = useState({});
  const [invClassCdList, setInvClassCdList] = useState(reduxViewState?.invClassCdList ?? []);
  const [disabledInvClass, setDisabledInvClass] = useState(false);
  const [sortParams, setSortParams] = useState(reduxViewState?.sortParams ?? {});

  const [weekHeader, setWeekHeader] = useState([]);
  const [originHeader, setOriginHeader] = useState([]);
  const [orderByItem, setOrderByItem] = useState(reduxViewState?.orderByItem ?? null);

  const [measureType, setMeasureType] = useState(reduxViewState?.measureType ?? AMT);
  const [p13Datas, setP13datas] = useState(reduxViewState?.p13Datas ?? {});
  const [measures, setMeasures] = useState(reduxViewState?.measures ?? []);
  const [displayName, setDisplayName] = useState({});

  const [measureModalOpen, setMeasureModalOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const pageSizes = [30, 50, 100];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize || pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);
  const gridRef = useRef();
  const EXCEL_EXPORT = "excelExport";

  const dataMap = useRef({});
  const workbenchThreshold = useRef({});
  const initSelectionDoneRef = useRef(false);
  const initialSelectVersionEffectDone = useRef(false);
  const measureEffectInitializedRef = useRef(false);
  const isRetrievingPlanActualRef = useRef(false);
  const inFlightPlanActualRequestKeyRef = useRef(null);
  const initialAutoRetrieveDoneRef = useRef(false);

  const allMeasures = useMemo(
    () => (isEqual(measureType, AMT) ? amtMeasure : qtyMeasure),
    [measureType, AMT, amtMeasure, qtyMeasure]);

  const defaultGridMemo = useMemo(() => {
    return {
      ...DpDefaultGridSetting({ title, viewName }),
      rowSelection: {
        mode: 'multiRow',
        checkboxes: false,
        headerCheckbox: false,
        enableClickSelection: true,
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chartSeries = () => {
    const seriesList = [];
    let colorIdx = 0;
    for (const chartMeasure of allMeasures) {
      let series = {};
      // chartData 의 key(setChart 의 row.measure)는 getMeasureDisplayName(result) ?? measureNm 로
      // 이미 표시명으로 변환돼 있으므로, field 도 같은 값으로 맞춰야 시리즈가 데이터를 찾는다.
      const measureField = displayName?.[chartMeasure] ?? chartMeasure;
      if (!includes(chartMeasure, "Per")) {
        series = {
          field: measureField,
          text: displayName?.[chartMeasure] ?? t(chartMeasure),
          type: codeUtil.toggleType.BAR,
          typeOptions: { fill: colorSet(colorIdx) },
          orientation: "left",
          formatter: numberUtil.formatNumber,
          groupName: t(measureType),
        }
      } else {
        series = {
          field: measureField,
          text: displayName?.[chartMeasure] ?? t(chartMeasure),
          type: codeUtil.toggleType.BAR,
          typeOptions: { fill: colorSet(colorIdx) },
          orientation: "right",
          formatter: num => `${num}%`,
          groupName: t("percent"),
        }
      }
      colorIdx++;
      seriesList.push(series);
    }
    return seriesList;
  }

  const onBtShowLoading = useCallback(() => {
    gridRef?.current?.api?.setGridOption('loading', true);
  }, []);

  const onBtHide = useCallback(() => {
    gridRef?.current?.api?.setGridOption('loading', false);
  }, []);

  const getRowStyle = params => {
    const measureOrder = size(measures) > 0 ? measures : isEqual(measureType, AMT) ? amtMeasure : qtyMeasure;
    // measureOrder 는 이름(번역 키) 목록이므로 라벨(measure)이 아니라 고정 이름(measureNm)과 비교한다.
    const rowMeasureNm = params?.data?.measureNm ?? params?.data?.measure;
    if (rowMeasureNm && isEqual(rowMeasureNm, measureOrder[0])) {
      return { background: green[50] };
    }
    return null;
  };

  const numberValueFormatBlank = (param, isExport = false) => {
    const { data, colDef } = param;
    const fieldName = colDef?.field;
    const value = data?.[fieldName];

    const expression = data?.resultRow?.customerId ? data?.resultRow?.customerCurrencyCd : data?.resultRow?.hrchyCurrencyCd;
    const symbol = getCurrencySymbol(expression) ?? userData?.currencySymbol ?? "";

    let formattedValue = numberUtil.formatNumber(value, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    if (!value || !formattedValue) {
      return "";
    }

    if (isExport) {
      if (includes(data?.measure, "Per")) {
        return numberUtil.formatNumber(value / 100, { maximumFractionDigits: 4, minimumFractionDigits: 4 }).replace(/,/g, "");
      }
      return formattedValue.replace(/,/g, "");
    }

    if (includes(data?.measure, "Per") && !isExport) {
      return `${formattedValue}%`;
    }

    if (typeof formattedValue === "string" && formattedValue.includes(".") && !isExport) {
      formattedValue = split(formattedValue, ".")[0];
    }

    const typeCd = data?.resultRow?.measure?.valueTypeCd;
    const isAmount = typeCd === codeUtil.dpMeasureType.AMT || typeCd === codeUtil.dpMeasureType.PRICE
      || data?.resultRow?.measureCd === DEMAND_PLAN_A.name || includes(amtMeasure, data?.resultRow?.measureCd);
    if (isAmount) {
      if (value < 0) {
        return `-${symbol}${replace(formattedValue, "-", "")}`;
      }
      return `${symbol}${formattedValue}`;
    }

    return formattedValue;
  };

  numberValueFormatBlank.customType = EXCEL_EXPORT;

  const measureValCellRenderer = ({ value }) => {
    return t(value);
  };

  const getFormatDate = index => {
    if (codeUtil.isWeekBucket(timeDimension)) {
      return weekHeader[originHeader[index]];
    } else {
      return dateUtils.headerDateFormatter(new ZDate(originHeader[index]), timeDimension);
    }
  };

  const setCurrencySymbol = (resultRow) => {
    const expression = resultRow?.customerId ? resultRow?.customerCurrencyCd : resultRow?.hrchyCurrencyCd;
    return getCurrencySymbol(expression) ?? userData?.currencySymbol ?? "";
  }

  const setChart = (selectedRows) => {
    if (selectedRows?.length < 1) {
      setChartData([]);
      return;
    }

    const rowList = [];
    for (const row of selectedRows) {
      // if (!includes(row.measure, "Per")) {
      rowList.push(row);
      // }
    }
    const chartDataList = [];
    for (let idx = 0; idx < originHeader.length; idx++) {
      const cData = {};
      let symbol = userData?.currencySymbol;
      for (const row of rowList) {
        if (size(row) > 0) {
          cData[row.measure] = row[formatHeaders[idx]];
        }
        symbol = setCurrencySymbol(row?.resultRow);
      }
      cData.yyyymmdd = getFormatDate(idx);
      cData.currency = symbol;
      chartDataList.push(cData);
    }
    setChartData(chartDataList);
  }

  const selectTopRowGroup = api => {
    const displayedRowCount = typeof api?.getDisplayedRowCount === "function" ? api.getDisplayedRowCount() : 0;
    if (displayedRowCount < 1 || typeof api?.getDisplayedRowAtIndex !== "function") {
      setChartData([]);
      return;
    }

    const firstDisplayedNode = api.getDisplayedRowAtIndex(0);
    const firstLeafNode = firstDisplayedNode?.allLeafChildren?.[0]
      ?? firstDisplayedNode?.parent?.allLeafChildren?.[0]
      ?? firstDisplayedNode;

    if (!firstLeafNode?.data) {
      setChartData([]);
      return;
    }

    const leafChildren = firstLeafNode.parent?.allLeafChildren?.length
      ? firstLeafNode.parent.allLeafChildren
      : [firstLeafNode];
    const selectedNodes = typeof api?.getSelectedNodes === "function" ? api.getSelectedNodes() : [];

    if (selectedNodes.length > 0) {
      api.setNodesSelected({ nodes: selectedNodes, newValue: false });
    }

    api.setNodesSelected({ nodes: leafChildren, newValue: true, source: CHECKBOX_SELECTED });
    api.ensureIndexVisible(firstLeafNode.rowIndex, "top");
    setChart(leafChildren.map(leaf => leaf.data).filter(Boolean));
  };

  const handelFilterClose = () => {
    setFilterModalOpen(false);
  };


  const onRowSelected = (params) => {
    if ((params.source === CHECKBOX_SELECTED || params.event) && params.node.parent.id !== "ROOT_NODE_ID" && params.rowIndex !== null) {
      const selectedRows = [];
      const selectedNodes = params.api.getSelectedNodes();

      const filterInfo = extractFilterInfo(params, t, gridRef);
      getFilterStore(viewName).setValue(filterInfo);

      const leafChildren = params.node?.allLeafChildren ?? params.node?.parent?.allLeafChildren;
      for (const leaf of leafChildren) {
        selectedRows.push(leaf.data);
      }

      params?.api?.setNodesSelected({ nodes: selectedNodes, newValue: false });
      params?.api?.setNodesSelected({ nodes: leafChildren, newValue: true });
      setChart(selectedRows);
    }
  };

  const initCheck = params => {
    if (!params?.api) {
      return;
    }

    if (initSelectionDoneRef.current) {
      return;
    }

    if (params.api.getDisplayedRowCount() < 1) {
      return;

    }

    initSelectionDoneRef.current = true;

    selectTopRowGroup(params.api);
  };

  const setColumnDefsByOrder = (order) => {

    const basicColDefs = [
      {
        headerName: CUSTHRCHY,
        showRowGroup: "custHrchyNm",
        colId: "custHrchyNmGrp",
        pinned: "left", lockPinned: true,
        width: 150,
        cellRenderer: "agGroupCellRenderer",
        cellClass: "stringType",
        cellRendererParams: { suppressCount: true },
        editable: false,
        sortable: true,
        sort: isEqual(sortParams?.orderByColumn, "cust_levelId") ? sortParams?.sortType : null,
      },
      { headerName: "custHrchyNm", field: "custHrchyNm", hide: true, suppressColumnsToolPanel: true, rowGroup: true,
        sort: isEqual(sortParams?.orderByColumn, "cust_levelId") ? sortParams?.sortType : null,
      },
      {
        headerName: "customerCd",
        showRowGroup: "custCd",
        colId: "custCdGrp",
        chartDataType: "category",
        width: 160,
        cellRenderer: "agGroupCellRenderer",
        pinned: "left", lockPinned: true,
        cellClass: "stringType",
        cellRendererParams: { suppressCount: true },
        editable: false,
        sortable: true,
        sort: isEqual(sortParams?.orderByColumn, "cust_CdId") ? sortParams?.sortType : null,
      },
      { headerName: "custCd", field: "custCd", hide: true, suppressColumnsToolPanel: true, rowGroup: true,
        sort: isEqual(sortParams?.orderByColumn, "cust_CdId") ? sortParams?.sortType : null,},
      {
        headerName: "customerName",
        showRowGroup: "custNm",
        colId: "custNmGrp",
        chartDataType: "category",
        width: 160,
        cellRenderer: "agGroupCellRenderer",
        pinned: "left", lockPinned: true,
        cellClass: "stringType",
        cellRendererParams: { suppressCount: true },
        editable: false,
        sortable: true,
        sort: isEqual(sortParams?.orderByColumn, "cust_levelId") ? sortParams?.sortType : null,
      },
      { headerName: "custNm", field: "custNm", hide: true, suppressColumnsToolPanel: true, rowGroup: true,
        sort: isEqual(sortParams?.orderByColumn, "cust_levelId") ? sortParams?.sortType : null,},
      {
        headerName: ITEMHRCHY,
        showRowGroup: "itemHrchyNm",
        colId: "itemHrchyNmGrp",
        chartDataType: "category",
        width: 190,
        cellRenderer: "agGroupCellRenderer",
        pinned: "left", lockPinned: true,
        cellClass: "stringType",
        cellRendererParams: { suppressCount: true },
        editable: false,
        sortable: true,
        sort: isEqual(sortParams?.orderByColumn, "productlevelId") ? sortParams?.sortType : null,
      },
      { headerName: "itemHrchyNm", field: "itemHrchyNm", hide: true, suppressColumnsToolPanel: true, rowGroup: true,
        sort: isEqual(sortParams?.orderByColumn, "productlevelId") ? sortParams?.sortType : null,
      },
      {
        headerName: "itemCd",
        showRowGroup: "itemCd",
        colId: "itemCdGrp",
        chartDataType: "category",
        width: 220,
        cellRenderer: "agGroupCellRenderer",
        pinned: "left", lockPinned: true,
        cellClass: "stringType",
        cellRendererParams: { suppressCount: true },
        editable: false,
        sortable: true,
        sort: isEqual(sortParams?.orderByColumn, "prod_CdId") ? sortParams?.sortType : null,
      },
      { headerName: "itemCode", field: "itemCd", hide: true, suppressColumnsToolPanel: true, rowGroup: true,
        sort: isEqual(sortParams?.orderByColumn, "prod_CdId") ? sortParams?.sortType : null
      },
      {
        headerName: "invClassCd",
        showRowGroup: "invClassCd",
        colId: "invClassId",
        chartDataType: "category",
        pinned: "left", lockPinned: true,
        cellRenderer: "agGroupCellRenderer",
        cellClass: "stringType",
        cellRendererParams: {
          suppressCount: true,
          // 등급 배지(칩)는 원래 틴트와 동일 조건 — 첫 measure 행에만. 그 외 measure 행은 평문 값 유지.
          innerRenderer: (params) => {
            const { value, data } = params;
            if (value && data && isEqual(measures?.[0], data.measureNm ?? data.measure)) {
              return <AbcXyzChipInline value={value} />;
            }
            return value ?? "";
          },
        },
        width: 130,
        sortable: true,
        sort: isEqual(sortParams?.orderByColumn, "inv_class_id") ? sortParams?.sortType : null,
      },
      { headerName: "invClassCode", field: "invClassCd", suppressColumnsToolPanel: true,  hide: true, rowGroup: true,
        sort: isEqual(sortParams?.orderByColumn, "inv_class_id") ? sortParams?.sortType : null,
      },
      {
        headerName: "itemName",
        showRowGroup: "itemNm",
        colId: "itemNmGrp",
        chartDataType: "category",
        width: 220,
        cellRenderer: "agGroupCellRenderer",
        pinned: "left", lockPinned: true,
        cellClass: "stringType",
        cellRendererParams: {
          suppressCount: true
        },
        editable: false,
        sortable: true,
        sort: isEqual(sortParams?.orderByColumn, "productlevelId") ? sortParams?.sortType : null,
      },
      { headerName: ITEM, field: "itemNm", hide: true, suppressColumnsToolPanel: true, rowGroup: true,
        sort: isEqual(sortParams?.orderByColumn, "productlevelId") ? sortParams?.sortType : null,
      },
      {
        headerName: "measure",
        field: "measure",
        width: 250,
        type: ["nonEditableColumn"],
        pinned: "left", lockPinned: true,
        cellRenderer: "agGroupCellRenderer",
        valueFormatter: measureValCellRenderer,
        headerComponent: CustomHeaderMenuIcon,
        columnGroupShow: "closed",
        cellRendererParams: { suppressCount: true },
        headerComponentParams: {
          enableMenu: false,
          isClicked: setMeasureModalOpen,
        },
      },
    ];

    if (filterValue?.category && size(filterValue?.category) > 0) {
      remove(basicColDefs, object => isEqual(object.headerName, "itemName") || isEqual(object.headerName, ITEM));
      remove(basicColDefs, object => isEqual(object.headerName, "itemCd") || isEqual(object.headerName, "itemCode"));
      remove(basicColDefs, object => isEqual(object.headerName, "invClassCd") || isEqual(object.headerName, "invClassCode"));
    } else {
      remove(basicColDefs, object => isEqual(object.headerName, ITEMHRCHY) || isEqual(object.headerName, "itemHrchyNm"));
    }

    if (filterValue?.customerHrchys && size(filterValue?.customerHrchys) > 0) {
      remove(basicColDefs, object => isEqual(object.headerName, "customerName") || isEqual(object.headerName, "custNm"));
      remove(basicColDefs, object => isEqual(object.headerName, "customerCd") || isEqual(object.headerName, "custCd"));
    } else {
      remove(basicColDefs, object => isEqual(object.headerName, CUSTHRCHY) || isEqual(object.headerName, "custHrchyNm"));
    }
    if (!order) {
      const orders = [CUSTHRCHY, "customerCd", "custCd", "custHrchyNm", "customerName", "custNm", ITEMHRCHY, "itemHrchyNm", "itemCd", "itemCode", "invClassCd", "invClassCode", "itemName", ITEM, "measure"];
      basicColDefs.sort((a, b) => orders.indexOf(a.headerName) - orders.indexOf(b.headerName));
    } else {
      const orders = [ITEMHRCHY, "itemHrchyNm", "itemCd", "itemCode", "invClassCd", "invClassCode", "itemName", ITEM, CUSTHRCHY, "custHrchyNm", "customerCd", "custCd", "customerName", "custNm", "measure"];
      basicColDefs.sort((a, b) => orders.indexOf(a.headerName) - orders.indexOf(b.headerName));
    }

    return basicColDefs;
  };

  const removeCol = (basicColDefs, columnNms) => {
    if (columnNms) {
      for (const colNm of columnNms) {
        remove(basicColDefs, object => isEqual(object.headerName, colNm));
      }
    }
  }

  const setExportColumnDefsByOrder = (order) => {
    const basicColDefs = [
      { field: "custHrchyNm", colId: "custHrchyNmGrpRef", hide: true, rowGroup: true, },
      { field: "custCd", colId: "custCdGrpRef", hide: true, rowGroup: true,  },
      { field: "custNm", colId: "custNmGrpRef", hide: true, rowGroup: true,  },
      { field: "itemHrchyNm", colId: "itemHrchyNmGrpRef", hide: true, rowGroup: true, },
      { field: "itemCd", colId: "itemCdGrpRef", hide: true, rowGroup: true,  },
      { field: "invClassCd", colId: "invClassCdGrpRef", hide: true, rowGroup: true, },
      { field: "itemNm", colId: "itemNmGrpRef", hide: true, rowGroup: true, },
      { field: "measure", colId: "measureRef", hide: true },
      { headerName: CUSTHRCHY, field: "custHrchyNm", colId: "custHrchyNmGrp",  width: 150 },
      { headerName: "customerCd", field: "custCd", colId: "custCdGrp", width: 150 },
      { headerName: "customerName", field: "custNm", colId: "custNmGrp", width: 150 },
      { headerName: ITEMHRCHY, field: "itemHrchyNm", colId: "itemHrchyNmGrp", width: 150 },
      { headerName: "itemCd", field: "itemCd", colId: "itemCdGrp", width: 150, cellClass: 'stringType' },
      { headerName: "invClassCd", field: "invClassCd", colId: "invClassCdGrp", width: 100, cellRenderer: AbcXyzChip },
      { headerName: "itemName", field: "itemNm", colId: "itemNmGrp", width: 150 },
      { headerName: "measure", field: "measure", colId: "measure", width: 160, valueFormatter: measureValCellRenderer },
    ];

    if (filterValue?.category && size(filterValue?.category) > 0) {
      const removeColumns = ["itemName", ITEM, "itemCd", "itemCode", "invClassCd", "invClassCode"];
      removeCol(basicColDefs, removeColumns);
    } else {
      remove(basicColDefs, object => isEqual(object.headerName, ITEMHRCHY) || isEqual(object.headerName, "itemHrchyNm"));
    }

    if (filterValue?.customerHrchys && size(filterValue?.customerHrchys) > 0) {
      remove(basicColDefs, object => isEqual(object.headerName, "customerName") || isEqual(object.headerName, "custNm"));
      remove(basicColDefs, object => isEqual(object.headerName, "customerCd") || isEqual(object.headerName, "custCd"));
    } else {
      remove(basicColDefs, object => isEqual(object.headerName, CUSTHRCHY) || isEqual(object.headerName, "custHrchyNm"));
    }
    if (!order) {
      const orders = [CUSTHRCHY, "customerCd", "customerName", ITEMHRCHY, "itemCd", "invClassCd", "itemName", "measure"];
      basicColDefs.sort((a, b) => orders.indexOf(a.headerName) - orders.indexOf(b.headerName));
    } else {
      const orders = [ITEMHRCHY, "itemCd", "invClassCd", "itemName", CUSTHRCHY, "customerCd",  "customerName", "measure"];
      basicColDefs.sort((a, b) => orders.indexOf(a.headerName) - orders.indexOf(b.headerName));
    }

    return basicColDefs;
  };


  const getStartDateByEndDate = (start, end) => {
    if (dateUtils.getDateWithoutTime(start) >= dateUtils.getDateWithoutTime(end)) {
      return new ZDate(end.getFullYear(), end.getMonth() - 6, 1, 0, 0, 0);
    }
    return start;
  };

  const setGridHeaders = (headers, bucket, order) => {
    const formatHeaderList = [];
    const colDefs = setColumnDefsByOrder(order);
    const exportColDefs = setExportColumnDefsByOrder(order);

    const setHeaderCols = (headerName, field) => {
      colDefs.push({
        headerName,
        field,
        colId: GridUtils.IGNORE_P13N_COLLID + '_' + field,
        width: 120,
        type: ["decimalPointColumn", "rightAligned"],
        chartDataType: "series",
        cellStyle: data => WorkbenchEditable.getAlertBackground(data, headerName) ? { backgroundColor: red[100] } : {},
        valueFormatter: numberValueFormatBlank,
        editable: false,
        cellDataType: false,
        cellClassRules: {
          alertBackground: data => WorkbenchEditable.getAlertBackground(data, headerName),
        },
      });

      exportColDefs.push({
        headerName,
        field,
        width: 120,
        valueGetter: params => numberValueFormatBlank(params, true),
        cellStyle: data => WorkbenchEditable.getAlertBackground(data, headerName) ? { backgroundColor: red[100] } : {},
        cellClass: params => {
          const value = Number(params?.value);
          return !isNaN(value) && !Number.isInteger(value) ? 'flexibleDemicalType' : '';
        },
        cellClassRules: {
          alertBackground: data => WorkbenchEditable.getAlertBackground(data, headerName),
        },
      });
    };

    if (codeUtil.isWeekBucket(bucket)) {
      const sortHeaders = Object.keys(headers).sort();
      for (const sortHeader of sortHeaders) {
        formatHeaderList.push(headers[sortHeader]);
        setHeaderCols(headers[sortHeader], headers[sortHeader]);
      }
    } else {
      if (isArray(headers)) {
        for (const header of headers) {
          const headerFormat = dateUtils.headerDateFormatter(new ZDate(header), bucket);
          formatHeaderList.push(headerFormat);
          setHeaderCols(headerFormat, headerFormat);
        }
      }
    }

    exportColumnDefs.current = exportColDefs;
    return { formatHeaderList, colDefs };
  };

  /*
   * 라벨은 두 곳을 모두 본다 — 우선순위 순서:
   *   1) z_plan_config(DP + MEASURE_DISPLAY_NAMES) 표시명. 저장 키는 MeasureType.getName()(고정 번역 키)
   *      이지만 서버가 준 name 은 마스터에서 바뀔 수 있으므로 measureCd 로 되찾은 이름으로도 조회한다.
   *   2) z_measure.name — measure 마스터에서 이름을 바꿨으면 그 이름을 그대로 보여준다.
   * 둘 다 없으면 null 을 돌려주고 호출부에서 고정 이름(번역 키)을 쓴다. 세 경우 모두 t() 를 거치는데
   * 언어팩에 없는 값은 t() 가 원문을 그대로 반환하므로 사용자 지정 이름도 그대로 노출된다.
   */
  const getMeasureDisplayName = obj => {
    const measureNm = obj?.measure?.name ?? obj?.measureCd;
    const rowMeasureCd = obj?.measure?.measureCd ?? obj?.measureCd;
    const canonicalNm = rowMeasureCd ? measureNmByCd[rowMeasureCd] : null;

    const custom = (canonicalNm ? displayName?.[canonicalNm] : null)
      ?? (measureNm ? displayName?.[measureNm] : null);
    if (custom && !isEmpty(custom)) {
      return custom;
    }

    const defaultNm = rowMeasureCd ? dpMeasureDefaultNmByCd[rowMeasureCd] : null;
    if (measureNm && defaultNm && !isEqual(measureNm, defaultNm)) {
      return measureNm;
    }
    return null;
  };

  const setMeasureNm = obj => {
    const rowMeasureCd = obj?.measure?.measureCd ?? obj?.measureCd;
    if (rowMeasureCd && measureNmByCd[rowMeasureCd]) {
      return measureNmByCd[rowMeasureCd];
    }
    let measureNm = obj?.measure?.name ? obj?.measure?.name : obj?.measureCd;
    if (isEqual(measureNm, MF_QTY.name)) {
      measureNm = DEMAND_PLAN_Q.name;
    } else if (isEqual(measureNm, MF_AMT.name)) {
      measureNm = DEMAND_PLAN_A.name;
    } else if (isEqual(measureNm, FF_QTY.name)) {
      measureNm = BUDGET_PLAN_Q.name;
    } else if (isEqual(measureNm, FF_AMT.name)) {
      measureNm = BUDGET_PLAN_A.name;
    }
    return measureNm;
  };

  const setGridDatas = (headers, results, type = AMT) => {
    const datas = [];
    if (!results?.data) {
      return datas;
    }

    for (const result of results.data) {
      if (!measures.includes(setMeasureNm(result))) {
        continue;
      }
      const measureNm = setMeasureNm(result);
      const row = { measure: getMeasureDisplayName(result) ?? measureNm, measureNm };
      row.custHrchyNm = result.displayParCustHrchy?.[0] ?? result.customerHrchyNm;
      row.custNm = result.customerNm ?? result.customerHrchyNm;
      row.custCd = result.customerCd ?? result.customerHrchyCd;
      row.itemHrchyNm = result.displayParItemHrchy?.[0] ?? result.itemHrchyNm;
      row.itemNm = result.itemNm ?? result.itemHrchyNm;
      row.itemCd = result.itemCd ?? result.itemHrchyCd;
      row.invClassCd = result.invClassCd;
      row.resultRow = result;
      row.currency = results.currency;

      if (!result?.pivotData) {
        datas.push(row);
        continue;
      }

      for (let i = 0; i < headers.length; i++) {
        const header = headers[i];
        if (typeof result.pivotData[i] !== "number") {
          if (result.pivotData[i]) {
            row[header] = result.pivotData[i];
          } else {
            row[header] = 0;
          }
        } else {
          row[header] = result.pivotData[i];
        }
      }
      datas.push(row);
    }

    const measureOrder = size(measures) > 0 ? measures : isEqual(type, AMT) ? amtMeasure : qtyMeasure;
    // 표시명을 바꾼 행도 원래 순서를 유지해야 하므로 라벨(measure)이 아니라 고정 이름(measureNm)으로 정렬한다.
    return datas.sort((a, b) => measureOrder.indexOf(a.measureNm) - measureOrder.indexOf(b.measureNm));
  }

  const getGridDataByType = (headers, results, type) => {
    const datas = setGridDatas(headers, results, type);
    setGridData(datas);
  }

  const setActiveMeasureCds = (measures) => {
    const activeMeasureCds = [];
    if (!isEmpty(measures)) {
      for (const key of Object.keys(dpPlanActualMeasures)) {
        const measureVal = dpPlanActualMeasures[key];
        if (measures.includes(measureVal.name)) {
          activeMeasureCds.push(measureVal?.cd ?? key);
        }
      }
    }
    return activeMeasureCds;
  }

  const handleLoadData = (response, order, versionBucket = null) => {
    responseRef.current = response;

    const timeBucket = versionBucket ?? timeDimension;

    if (!response.resultData?.data) {
      setGridData([]);
      return;
    } else {

      const { header: headers } = response.resultData;
      let sortHeaders = headers;
      if (codeUtil.isWeekBucket(timeBucket)) {
        sortHeaders = Object.keys(headers).sort();
      }

      workbenchThreshold.current = response?.resultData?.workbenchThreshold ?? {};

      const gridHeaders = setGridHeaders(headers, timeBucket, order);
      getGridDataByType(gridHeaders.formatHeaderList, response.resultData, measureType);
      setFormatHeaders(gridHeaders.formatHeaderList);
      setColumnDefs(gridHeaders.colDefs);
      setWeekHeader(headers);
      setOriginHeader(sortHeaders);

      const results = cloneDeep(response.resultData?.data);
      dataMap.current = WorkbenchUtil.makeDataMap({ results, timeDimension: timeBucket, formatHeaders: headers });
    }

    const searchEndDate = new ZDate(response.endDate);
    if (!isEqual(searchEndDate, endDate)) {
      setEndDate(searchEndDate);
      const changeStartDate = new ZDate(response.startDate);
      setStartDate(dateUtils.nearDay(new ZDate(changeStartDate), planConfigObj?.START_DAY || codeUtil.dayOfWeek.MONDAY.cd, timeBucket));
    } else {
      let searchStartDate = startDate;
      if (!isEqual(searchStartDate, new ZDate(response.startDate))) {
        searchStartDate = (dateUtils.nearDay(new ZDate(response.startDate), planConfigObj?.START_DAY || codeUtil.dayOfWeek.MONDAY.cd, timeBucket));
      }

      if (!isEqual(searchStartDate, startDate)) {
        setStartDate(searchStartDate);
      }
    }

    const pageResult = response?.resultData?.page;
    if (pageResult) {
      setGridPages(pageResult.totalPages);
    }
  };

  const retrievePlanVersion = () => {
    planConfigService.getDpPlanConfig()
      .then((response) => {
        if (response?.data?.data) {
          const configObj = response?.data?.data?.planConfigObj;
          setPlanConfigObj(configObj);
        }
      }).catch(e => {
      debug(e);
    })
      .finally(() => {
        setLoading(true);
      });
  };

  const getRequestParams = (orderByItemParam, versionBucket) => {
    const custFilter = filterValue?.customerHrchys?.map(data => data?.hrchyCd) ?? null;
    const itemFilter = filterValue?.category?.map(data => data?.hrchyCd) ?? null;
    const timeBucket = versionBucket ?? timeDimension;
    const params = {
      versionId: selectVersion?.id ?? null,
      custLevelCd: filterValue.customerHrchys?.[0]?.hrchyLv ?? null,
      itemLevelCd: filterValue.category?.[0]?.hrchyLv ?? null,
      custHrchyColumn: 1,
      itemHrchyColumn: 1,
      customerHrchyType: false,
      headerType: true,
      custFilter,
      itemFilter,
      timeDimension: timeBucket,
      startDate: format(WorkbenchUtil.getSearchFormat(startDate, timeBucket), 'yyyy-MM-dd'),
      endDate: format(WorkbenchUtil.getSearchFormat(endDate, timeBucket),'yyyy-MM-dd'),
      bottomLevel: isEmpty(custFilter)  && isEmpty(itemFilter),
      planningRange: 0,
      customerFilterList,
      itemFilterList,
      custFilterType,
      itemFilterType,
      invClassCdList,
      singleMeasure: false,
      rowMeasure: {},
      sortType,
      orderByItem: orderByItemParam ?? orderByItem,
      isCum,
      cum: isCum,
      activeMeasure: setActiveMeasureCds(measures),
    };

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (reload) {
      params.page = 0;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    const checkSortParms = sortParams?.sortType ? sortParams : {};
    return  { ...params, ...checkSortParms };
  };

  const retrievePlanActual = (order = null, versionBucket = null) => {
    const params = getRequestParams(order, versionBucket);
    const requestKey = axiosUtils.jsonbigStringfy(params);

    // Prevent duplicate API calls when multiple effects fire with identical params during init/re-entry.
    if (isRetrievingPlanActualRef.current && isEqual(inFlightPlanActualRequestKeyRef.current, requestKey)) {
      return;
    }

    isRetrievingPlanActualRef.current = true;
    inFlightPlanActualRequestKeyRef.current = requestKey;

    initSelectionDoneRef.current = false;
    exportColumnDefs.current = [];
    setColumnDefs([]);
    onBtShowLoading();

    if (params?.itemLevelCd === null) {
      setDisabledInvClass(false);
    }
    DpPlanActualService.getAll(params)
      .then(response => {
        if (!isEmpty(response.data)) {
          handleLoadData(response.data, order, versionBucket);
        } else {
          setGridData([]);
          setChartData([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
      })
      .catch(e => {
        const message = e.response?.data?.message;
        setSnackInfo({ open: true, severity: message?.type || snackBarMessageColor.networkError, content: t(message?.message) || t("MSG_NetworkError") });
        debug(e);
      })
      .then(() => {
        setTreeload(false);
        setItemTreeLoad(false);
        const reduxState = reduxUtil.createObjectState(viewName, { ...params,
          planConfigObj, startDate, endDate, currentPage, pageSize, sortParams,
          selectVersion, filter: filterValue, measureType, measures,
          customerFilterList, itemFilterList, itemFilterType, custFilterType, invClassCdList, timeDimension: versionBucket ?? timeDimension,
        });
        reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));
        onBtHide();
        setReload(false);
      })
      .finally(() => {
        if (isEqual(inFlightPlanActualRequestKeyRef.current, requestKey)) {
          isRetrievingPlanActualRef.current = false;
          inFlightPlanActualRequestKeyRef.current = null;
        }
      });
  };

  const saveOrderByItem = (orderByItemParam) => {
    const orderPageCd = isCum ? DP_ACTUAL_PLAN_CUM_ORDER_ITEM : DP_ACTUAL_PLAN_ORDER_ITEM;
    const saveData = { pageCd: orderPageCd , configObj: { orderByItem: orderByItemParam } };

    p13nService.upsert(saveData)
      .then(() => {
        debug(t("MSG_SaveSuccess"));
      })
      .catch(e => {
        debug(e);
      });
  }

  const changeSortDimen = () => {
    setOrderByItem(!orderByItem);
    retrievePlanActual(!orderByItem);
    saveOrderByItem(!orderByItem);
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrievePlanActual();
    }
  };

  const setDateByTimeDimension = () => {
    let start;
    let end = endDate;
    if (isEqual(DAY, timeDimension)) {
      start = new ZDate(end.getFullYear(), end.getMonth() - 1, end.getDate(), 0, 0, 0);
    } else {
      end = TODAY;
      start = getStartDateByEndDate(startDate, end);
    }
    setStartDate(start);
    setEndDate(end);
    setReload(true)
  };

  const filterActionItem = params => {
    if (params.type === "click") {
      setModelCode(item.name);
      setFilterModalOpen(true);
    }
  };

  const getFilteredMeasures = (type, data) => {
    if (data?.[`measure${type}`]) return data?.[`measure${type}`];
    return allMeasures;
  }

  const getMeasure = () => {
    p13nService.get(viewName).then(response => {
      const { data } = response;
      if (data) {
        setMeasures(getFilteredMeasures(measureType, data));
        setP13datas(data);
      }
    })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        retrievePlanVersion();
        const reduxState = reduxUtil.createObjectState(viewName, { p13Datas });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      });
  }

  const getOrderByItem = () => {
    const orderPageCd = isCum ? DP_ACTUAL_PLAN_CUM_ORDER_ITEM : DP_ACTUAL_PLAN_ORDER_ITEM;
    p13nService.get(orderPageCd)
      .then(response => {
        const { data } = response;
        setOrderByItem(data?.orderByItem ?? false);
      })
      .catch(e => {
        debug(e);
      })
  }

  // DpSettings.js "Measure Display Name" 설정 — ChannelWorkbench.js와 동일한 패턴.
  /*
   * 표시명은 두 곳에서 온다 — 둘 다 고정 이름(번역 키) 기준으로 하나의 맵에 합친다.
   *   1) z_measure.name — measure 마스터에서 이름을 바꾼 경우 (BfAccuracyReport 와 동일 방식)
   *   2) z_plan_config(DP + MEASURE_DISPLAY_NAMES) 표시명 — 더 명시적인 설정이므로 1)을 덮어쓴다
   * 키를 고정 이름으로 유지하는 이유는 allMeasure / measures / grid 의 measure 식별자가 모두
   * 고정 이름이기 때문이다. 식별자 자체는 절대 표시명으로 바꾸지 않는다.
   */
  const retrieveMeasureDisplayNames = () => {
    // codeUtil.dpMeasure 는 이 화면이 쓰는 measure 만 담은 부분집합이라 채널 전용 measure
    // (TARGET_INV_DAYS 등)가 빠진다. measureService.getDisplayNameList() 는 채널과 무관하게
    // DP 표시명 대상 measure 전체(채널 + plan-vs-actual)를 내려주므로 기본 이름 소스로 이걸 쓴다.
    const zMeasureNames = Promise.all([measureService.getAll({isBasic: true}), measureService.getDisplayNameList()])
      .then(([measuresResponse, displayNameListResponse]) => {
        const results = measuresResponse?.data?.results;
        const displayNameMeasures = displayNameListResponse?.data;
        const nextDisplayNames = {};
        if (isEmpty(results) || isEmpty(displayNameMeasures)) {
          return nextDisplayNames;
        }
        const nameByMeasureCd = {};
        for (const measure of results) {
          nameByMeasureCd[measure.measureCd] = measure.name;
        }
        for (const measure of displayNameMeasures) {
          const zMeasureName = nameByMeasureCd[measure.measureCd];
          // 기본 이름과 다를 때만 = 마스터에서 실제로 바꾼 경우만 표시명으로 취급한다.
          if (zMeasureName && !isEqual(zMeasureName, measure.measure)) {
            nextDisplayNames[measure.measure] = zMeasureName;
            // MF_QTY 처럼 이 화면에서만 다른 이름(demandPlanQty)으로 쓰는 measure 는 그 이름으로도 넣는다.
            // allMeasure / measures 가 그 이름을 쓰므로 이 키가 없으면 모달에 표시명이 안 붙는다.
            const planActualNm = measureNmByCd[measure.measureCd];
            if (planActualNm) {
              nextDisplayNames[planActualNm] = zMeasureName;
            }
          }
        }
        return nextDisplayNames;
      })
      .catch(e => {
        debug(e);
        return {};
      });

    const configNames = planConfigService.getMeasureDisplayNames(CodeUtil.module.DP)
      .then(response => {
        const { data } = response;
        return data && !isEmpty(data?.objVal) ? data.objVal : {};
      })
      .catch(e => {
        debug(e);
        return {};
      });

    Promise.all([zMeasureNames, configNames])
      .then(([fromMeasure, fromConfig]) => {
        setDisplayName({ ...fromMeasure, ...fromConfig });
      });
  };

  const applyMeasures = selectedMeasures => {
    const data = {};
    data[`measure${measureType}`] = selectedMeasures;
    setP13datas({...p13Datas, ...data})
    setMeasures(selectedMeasures);
  };

  const getDisplayName = (measures) => {
    const names = [];
    for (const measure of measures) {
      names.push(displayName?.[measure] ?? measure);
    }
    return names;
  }

  const onGridReady = params => {
    DataState.initialize(gridRef.current?.api);
    GridUtils.setColumnDefs({ ...gridRef.current, columnDefs, viewName, initState: true  });
    setGridApi(params.api);
  };

  const onCellGridClicked = e => {
    const selected = e.node.allLeafChildren ? e.node.allLeafChildren : e.node.parent.allLeafChildren;
    e?.api?.setNodesSelected({ nodes: selected, newValue: true, source: CHECKBOX_SELECTED });
  };

  const onChangePeriod = (newValue, target) => {
    if (newValue) {
      if (isEqual(target, PERIOD_START)) {
        setStartDate(newValue)
      } else if (isEqual(target, PERIOD_END)) {
        setEndDate(newValue)
      }
    }
  };

  const handleChangeTimeBucket = (_, newAlignment) => {
    if (!isNull(newAlignment)) {
      if (newAlignment) {
        if (newAlignment === WEEK && planConfigObj?.BUCKET_UNIT === PARTIAL_WEEK) {
          newAlignment = PARTIAL_WEEK;
        }
      }
      setTimeDimension(newAlignment);
    }
  };

  const filterActionCustomer = params => {
    if (params.type === "click") {
      setModelCode(customer.name);
      setFilterModalOpen(true);
    }
  };

  const settingSortParams = (params) => {
    const columnState = params.api.getColumnState();
    let sortState = sortParams ?? {};

    const custHrchy = "custHrchyNmGrp";
    const customer = "custNmGrp";
    const itemHrchy = "itemHrchyNmGrp";
    const item = "itemNmGrp";

    const sortColumn = find(columnState, column => isEqual(column.sort, "asc") || isEqual(column.sort, "desc"));
    if (isEqual(params.source, "uiColumnSorted")) {
      let sortColNm = sortColumn?.colId;
      if (includes([custHrchy, customer], sortColumn?.colId)) {
        sortColNm = "cust_levelId";
      } else if (includes([itemHrchy, item], sortColumn?.colId) ) {
        sortColNm = "productlevelId";
      } else if (isEqual("invClassId", sortColumn?.colId)) {
        sortColNm = "inv_class_id";
      } else if (isEqual("itemCdGrp", sortColumn?.colId)) {
        sortColNm = "prod_CdId";
      } else if (isEqual("custCdGrp", sortColumn?.colId)) {
        sortColNm = "cust_CdId";
      }

      sortState = { orderByColumn: sortColNm, sortType: sortColumn?.sort };
    }
    setSortParams(sortState);
  };

  const isReadyCondition = !isNull(orderByItem) && startDate && endDate && timeDimension && size(measures) > 0;

  useEffect(() => {
    if (loading && !isEmpty(gridRef.current)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs, viewName, initState: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, columnDefs])

  useEffect(() => {
    if (isNull(orderByItem)) {
      getOrderByItem();
    }
    if (isEmpty(measures)) {
      getMeasure();
    } else {
      setLoading(true);
    }
    retrieveMeasureDisplayNames();

    DataState.initialize(gridRef.current?.api);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isNull(reduxViewState?.orderByItem) && loading) {
      if (initialAutoRetrieveDoneRef.current) {
        return;
      }

      initialAutoRetrieveDoneRef.current = true;
      if (selectVersion?.bucket && !isEqual(selectVersion?.bucket, timeDimension)) {
        setTimeDimension(selectVersion?.bucket);
      }
      retrievePlanActual(orderByItem, selectVersion?.bucket);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    if (reload && isReadyCondition) {
      initPageToRetrieve();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload])

  useEffect(() => {
    if ((!isEqual(reduxViewState?.currentPage, currentPage) || !isEqual(reduxViewState?.pageSize, pageSize) || (!isEqual(reduxViewState?.sortParams, sortParams)))
      && isReadyCondition) {
      retrievePlanActual(orderByItem);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, sortParams]);

  useEffect(() => {
    if (reduxViewState?.timeDimension && !isEqual(reduxViewState?.timeDimension, timeDimension)) {
      setDateByTimeDimension();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeDimension]);

   useEffect(() => {
     if (!initialSelectVersionEffectDone.current) {
       initialSelectVersionEffectDone.current = true;
       return;
     }
     
     if (loading && selectVersion?.id && isReadyCondition) {
       if (!isEqual(selectVersion?.bucket, timeDimension)) {
         setTimeDimension(selectVersion?.bucket);
       }
       const isVersionChanged = !isEqual(reduxViewState?.selectVersion, selectVersion);
       const canRunInitialAutoRetrieve = !initialAutoRetrieveDoneRef.current;

       if (isVersionChanged || canRunInitialAutoRetrieve) {
         initialAutoRetrieveDoneRef.current = true;
         retrievePlanActual(null, selectVersion?.bucket);
       }
     }
     // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [selectVersion])

  useEffect(() => {
    if (!isEmpty(filterValue) && loading) {
      if (filterValue?.customerHrchys?.length > 0) {
        setCustomerFilterList({});
        setCustomerFilterColor(muicolor.DEFAULT)
      }
      if (filterValue?.category?.length > 0) {
        setItemFilterList({});
        setItemFilterColor(muicolor.DEFAULT);
      }

      if (size(filterValue?.category) > 0) {
        setInvClassCdList([]);
        setDisabledInvClass(true);
      } else {
        setDisabledInvClass(false);
      }

      setReload(true);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue]);

  useEffect(() => {
    if (size(filterData) > 0) {
      if (filterData?.modelCode === customer.name) {
        setCustomerFilterList(filterData.filterList);
        setCustomerFilterColor(size(filterData?.filterList) > 0 ? muicolor.INFO : muicolor.DEFAULT);
        setFilterValue({ ...filterValue, customerHrchys: null});
        setCustomValue([]);
        setTreeload(true);
        setCustFilterType(filterData.filterType);
      } else {
        setItemFilterList(filterData.filterList);
        setItemFilterColor(size(filterData?.filterList) > 0 ? muicolor.INFO : muicolor.DEFAULT);
        setFilterValue({ ...filterValue, category: null});
        setItemCustomValue([]);
        setItemTreeLoad(true);
        setItemFilterType(filterData.filterType);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterData]);

  useEffect(() => {
    if (reduxViewState?.invClassCdList) {
      if (!isEqual(reduxViewState?.invClassCdList, invClassCdList)) {
        setReload(true);
      }
    }
    const reduxState = reduxUtil.createObjectState(viewName, { invClassCdList });
    reduxDispatch(reduxUtil.updateViewState(reduxState));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invClassCdList]);

  useEffect(() => {
    if (reduxViewState?.measureType && isReadyCondition) {

      setMeasures(getFilteredMeasures(measureType, p13Datas));

      const reduxState = reduxUtil.createObjectState(viewName, { measureType });
      reduxDispatch(reduxUtil.updateViewState(reduxState));
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureType]);

  useEffect(() => {
    if (!isEmpty(p13Datas)) {
      setMeasures(getFilteredMeasures(measureType, p13Datas));

      const reduxState = reduxUtil.createObjectState(viewName, { p13Datas });
      reduxDispatch(reduxUtil.updateViewState(reduxState));
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p13Datas]);

   useEffect(() => {
     if (!loading || size(measures) === 0) {
       return;
     }
     const prevMeasures = reduxViewState?.measures ?? [];
     const isFirstMeasureSync = !measureEffectInitializedRef.current;
     if (isFirstMeasureSync) {
       measureEffectInitializedRef.current = true;
     }

     if (reduxViewState?.measures) {
       const reduxState = reduxUtil.createObjectState(viewName, { measures });
       reduxDispatch(reduxUtil.updateViewState(reduxState));
     }

     if (!isFirstMeasureSync && !isEqual(prevMeasures, measures)) {
       setReload(true);
     }
     //eslint-disable-next-line react-hooks/exhaustive-deps
   }, [measures, loading]);

  // rowData가 변경될 때마다 초기화
  useEffect(() => {
    getFilterStore(viewName).clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridData, viewName]);

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        {
          statusPanel: FilterStatusPanel,
          statusPanelParams: {
            viewName
          },
          align: "left",
          key: "filterStatusPanel"
        },
        // { statusPanel: SumBigNumberValue },
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ]
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FilterContainer>
        <Box>
          <Tooltip title={t("dimension")}>
            <IconButton
              variant="contained"
              onClick={changeSortDimen}
            >
              <Transform />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <DpVersionSelect
            selectedVersion={setSelectVersion}
            status={[RELEASED]}
            reduxVersion={reduxViewState?.selectVersion ?? null}
            reload={vListReload}
            setReload={setVListReload}
          />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t(PERIOD_START)}
              views={codeUtil.isWeekBucket(timeDimension) || isEqual(timeDimension, DAY) ? ["year", "month", "day"] : ["year", "month"]}
              format={codeUtil.isWeekBucket(timeDimension) || isEqual(timeDimension, DAY) ? "yyyy-MM-dd" : "yyyy-MM"}
              maxDate={endDate}
              value={startDate}
              openTo={"month"}
              shouldDisableMonth={(e) => WorkbenchUtil.ableMonthOfQuerter(e, timeDimension)}
              shouldDisableDate={(e) => WorkbenchUtil.ableDayOfWeek(e, timeDimension, planConfigObj?.START_DAY)}
              onAccept={newValue => {
                onChangePeriod(newValue, PERIOD_START);
                setReload(true);
              }}
              slotProps={{ textField: { size: "small", sx: { mr: 1, width: 150 } } }}
            />
            <DatePicker
              label={t(PERIOD_END)}
              views={codeUtil.isWeekBucket(timeDimension) || isEqual(timeDimension, DAY) ? ["year", "month", "day"] : ["year", "month"]}
              format={codeUtil.isWeekBucket(timeDimension) || isEqual(timeDimension, DAY) ? "yyyy-MM-dd" : "yyyy-MM"}
              minDate={startDate}
              maxDate={MAX_END_DATE}
              value={endDate}
              openTo={"month"}
              shouldDisableMonth={(e) => WorkbenchUtil.ableEndMonthOfQuerter(e, timeDimension)}
              shouldDisableDate={(e) => WorkbenchUtil.ableEndDayOfWeek(e, timeDimension, planConfigObj?.START_DAY)}
              onAccept={newValue => {
                onChangePeriod(newValue, PERIOD_END);
                setReload(true);
              }}
              slotProps={{ textField: { size: "small", sx: { mr: 1, width: 150 } } }}
            />
          </LocalizationProvider>
          <BucketToggleButton value={timeDimension} onChange={handleChangeTimeBucket} buckets={[DAY, WEEK, MONTH, QUARTER]} />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <CustomerHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} customValue={customValue} treeload={treeload} enableMultiSelect styles={{ width: 290, ml: 0.5 }}/>
          <IconButton
            aria-describedby={"custHrchyPop"}
            size="small"
            sx={{ mr: 1 }}
            onClick={filterActionCustomer}
            color={customerFilterColor}>
            <Tooltip title={t("custFilter")}>
              <FilterAlt />
            </Tooltip>
          </IconButton>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <ItemHrchyTreeFilter viewName={viewName} onChangeFilter={setFilterValue} customValue={itemCustomValue} treeload={itemTreeLoad} enableSelectParent enableMultiSelect styles={{ width: 290 }}/>
          <IconButton
            aria-describedby={"prodHrchyPop"}
            size="small"
            sx={{ mr: 1 }}
            onClick={filterActionItem}
            color={itemFilterColor}>
            <Tooltip title={t("itemFilter")}>
              <FilterAlt />
            </Tooltip>
          </IconButton>
        </Box>
        <AbcXyzFilterModal viewName={viewName} invClassCdList={invClassCdList} setInvClassCdList={setInvClassCdList} disabled={disabledInvClass} />
        {/* <Box>
        <ResetButton onClick={clickReset} />
      </Box> */}
        <Box sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
          <TypeToggleButton toggleType="measure" initialValue={measureType} toggleValue={setMeasureType}></TypeToggleButton>
          <Box sx={{ ml: 1 }}>
            <DpExcelButtons
              gridApi={gridApi}
              responseRef={responseRef.current}
              columnDefs={GridUtils.getColumnDefs(exportColumnDefs.current)}
              params={selectVersion ? getRequestParams() : null}
              filter={{
                version: selectVersion,
                timeDimension,
                formatHeaders,
                order: orderByItem,
                workbenchThreshold: workbenchThreshold.current,
                measures
              }}
              isDpPlanActual
            />
          </Box>
        </Box>
      </FilterContainer>
      <Box className={"grid-area"}>
        <SplitterContainer collapseDirection="up" initialSizes={[2, 3]}>
          <Box className="chart-area">
            <ChartComposed
              xAxis={["yyyymmdd"]}
              data={chartData}
              dataSeries={chartSeries()}
              tooltipAnimation={false}
              options={{ minHeight: "200px", isAmt: isEqual(measureType, AMT) }}
              activeMeasures={getDisplayName(measures)}
            />
          </Box>
          <Box sx={{ height: "100%" }}>
            <Box
              className="ag-theme-balham non-background-selected-row"
              mb={1}
              sx={{ height: "calc(100% - 60px)", mt: 2 }}>
              <AgGridReact
                ref={gridRef}
                rowData={gridData}
                onGridReady={onGridReady}
                {...defaultGridMemo}
                rowSelection={{
                  ...defaultGridMemo.rowSelection,
                  groupSelects: "descendants",
                }}
                getRowStyle={getRowStyle}
                onRowSelected={onRowSelected}
                onCellClicked={onCellGridClicked}
                groupHideOpenParents
                animateRows
                excelStyles={WorkbenchUtil.excelStyles}
                groupDisplayType={"custom"}
                groupDefaultExpanded={-1}
                statusBar={statusBar}
                overlayLoadingTemplate={`<span class="ag-overlay-loading-center">${t("MSG_Loading")}</span>`}
                onRowDataUpdated={initCheck}
                suppressAggFuncInHeader={false}
                onSortChanged={params => settingSortParams(params)}
                context={{
                  workbenchThreshold: workbenchThreshold.current,
                  headerList: formatHeaders,
                  dataMap: dataMap.current
                }}
              />
            </Box>
            <PaginationContainer
              currentPage={currentPage}
              totalPages={gridPages}
              pageSize={pageSize}
              pageSizes={pageSizes}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
            />
          </Box>
        </SplitterContainer>
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <MeasureModal
        open={measureModalOpen}
        setOpen={setMeasureModalOpen}
        viewName={viewName}
        allMeasure={allMeasures}
        measureType={measureType}
        applyMeasures={applyMeasures}
        displayNames={displayName}
        hasDisplayName={true}
      />
      <DPFilterModal
        open={filterModalOpen}
        close={handelFilterClose}
        displayModButtons
        modelCode={modelCode}
        modalResultData={setFilterData}
        filteredList={{ Customer: customerFilterList, Item: itemFilterList }}
        filterTypes={{ Customer: custFilterType, Item: itemFilterType }}
      />
    </>
  );
};

export default withTranslation()(DpPlanVsActual);
